import Constants from 'expo-constants';
import { useRouter } from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import React, { useEffect } from 'react';
import { Alert, Platform, ScrollView, View } from 'react-native';
import { TapGestureHandler } from 'react-native-gesture-handler';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useCustomer } from '@/api/customers/use-customer';
import { useAdmin } from '@/api/partners/admins/use-admin';
import { useModalStore } from '@/components/login-modal/store';
import { useAuth } from '@/core';
import { clearAll } from '@/core/storage';
import useSettingsStore from '@/stores/use-settings-store';
import { Button, Card, Text } from '@/ui';
import { ConfirmModal } from '@/ui/confirm-modal';
import { Docs, Exit, Info, Person } from '@/ui/icons';
import { Bank } from '@/ui/icons/bank';
import { Bolt } from '@/ui/icons/bolt';
import { ChevronRight } from '@/ui/icons/chevron-right';
import { Marker } from '@/ui/icons/marker';
import { PersonPlus } from '@/ui/icons/person-plus';
import { QuestionMark } from '@/ui/icons/question-mark';
import { Loader } from '@/ui/loader';
import { MenuItem } from '@/ui/menu-item';

export default function More() {
  const { styles, theme } = useStyles(stylesheet);
  const status = useAuth.use.status();
  const loginMode = useModalStore((state) => state.loginModalMode);
  const setLoginModalMode = useModalStore((state) => state.setLoginModalMode);
  const setPartnerMode = useSettingsStore((state) => state.setPartnerMode);
  const router = useRouter();

  const [clearCacheModalVisible, setClearCacheModalVisible] =
    React.useState(false);

  const handleLogout = () => {
    setPartnerMode(false);
    signOut();
    router.replace('/search');

    Toast.show({
      text1: 'You have logged out',
      type: 'success',
    });
  };

  const {
    data: customer,
    isLoading: isGettingCustomer,
    isError: isGettingCustomerError,
    refetch: refetchCustomer,
  } = useCustomer({
    retry: false,
  });
  const { data: admin } = useAdmin();

  useEffect(() => {
    if (status === 'signIn') {
      const fetchCustomer = async () => {
        await refetchCustomer();
      };

      fetchCustomer();
    }
  }, [status, customer]);

  const signOut = useAuth.use.signOut();

  const clearCache = () => {
    if (Platform.OS === 'web') {
      setClearCacheModalVisible(true);
      return;
    }

    Alert.alert('Clear cache?', 'Do it.', [
      {
        text: 'Cancel',
      },
      {
        text: 'Delete',
        onPress: () => {
          clearAll();
        },
        style: 'destructive',
      },
    ]);
  };

  const handleOpenReferral = () => {
    router.push('/account/referrals');
  };

  if (isGettingCustomer) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ width: 150, height: 150 }} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        style={{ flexGrow: 1 }}
        showsVerticalScrollIndicator={false}
      >
        <View>
          <Text type="title1">
            {customer?.firstname
              ? `${customer?.firstname} ${customer?.lastname}`
              : ''}{' '}
          </Text>

          <Text
            type="bodyRegular"
            style={{ textDecorationLine: 'none', userSelect: 'none' }}
            selectable={false}
          >
            {customer?.email}
          </Text>
        </View>

        <Card>
          <MenuItem
            leftIcon={<Person />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="account.profile"
            onPress={() => {
              router.push('/partner/more/profile');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Bank />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="partner.more.bankAccount"
            onPress={async () => {
              router.push('/partner/more/bank-account');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Docs />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="partner.more.ownerGuide"
            onPress={async () => {
              const url =
                'https://eonrides.notion.site/Eon-Guide-For-Owners-30874a0bcebc4894819b5ecf62cf6b4f';

              if (Platform.OS === 'web') {
                window.open(url, '_blank');
              } else {
                await WebBrowser.openBrowserAsync(url);
              }
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Marker />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="partner.more.locations"
            onPress={() => {
              router.push('/partner/more/locations');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Info />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            style={{ width: '100%' }}
            tx="account.supportFaq"
            onPress={() => {
              router.push('/partner/more/support-faq');
            }}
          />

          <MenuItem
            leftIcon={<Bolt />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="partner.more.whatsNew"
            onPress={() => {
              router.push('/partner/more/whats-new');
            }}
            style={{ width: '100%' }}
            badgeCount={admin?.unreadChangelogCount}
          />
        </Card>

        <Card>
          <MenuItem
            leftIcon={<PersonPlus />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="partner.more.referAFriend"
            onPress={() => {
              router.push('/partner/more/referrals');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<QuestionMark />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="partner.sidebar.howEonWorks"
            onPress={() => {
              router.push('/partner/cars?onboarding=1');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Person />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            tx="partner.more.submitFeedback"
            onPress={() => {
              router.push('/partner/more/submit-feedback');
            }}
            style={{ width: '100%' }}
          />

          <MenuItem
            leftIcon={<Docs />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            style={{ width: '100%' }}
            tx="account.legal"
            onPress={() => {
              router.push('/partner/more/legal');
            }}
          />

          <MenuItem
            leftIcon={<Exit />}
            leftIconFillColor={theme.colors.icon.color}
            rightIcon={<ChevronRight />}
            rightIconFillColor={theme.colors.icon.color}
            style={{ width: '100%' }}
            tx="account.logout"
            onPress={handleLogout}
          />
        </Card>

        <View style={{ alignItems: 'center', marginTop: 16 }}>
          <Text type="caption1" style={{ color: theme.colors.text.secondary }}>
            Version {Constants.expoConfig?.version}
          </Text>
        </View>

        <View style={{ alignItems: 'center' }}>
          <Button
            text="Switch to guest"
            type="primaryExtraSmall"
            onPress={() => {
              setPartnerMode(false);
              router.replace('/search');
            }}
          />
        </View>

        <TapGestureHandler onActivated={clearCache} numberOfTaps={3}>
          <View style={{ width: 100, height: 100 }} />
        </TapGestureHandler>

        <ConfirmModal
          isVisible={clearCacheModalVisible}
          titleTx="account.clearCache"
          bodyTx="account.clearCache"
          leftButton={{
            tx: 'account.cancel',
            onPress: () => {
              setClearCacheModalVisible(false);
            },
          }}
          rightButton={{
            tx: 'account.clearCache',
            onPress: () => {
              clearAll();
              setClearCacheModalVisible(false);
            },
          }}
          onDismiss={() => {
            setClearCacheModalVisible(false);
          }}
        />
      </ScrollView>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    backgroundColor: theme.colors.background_1,
    flex: 1,
    alignItems: {
      sm: 'center',
    },
    paddingTop: UnistylesRuntime.insets.top,
  },
  scrollContainer: {
    flexGrow: 1,
    paddingTop: 24,
    paddingLeft: 16,
    paddingRight: 16,
    gap: 16,
    backgroundColor: theme.colors.background_1,
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: 600,
    },
    width: {
      xs: '100%',
      sm: 600,
      md: 600,
    },
  },
}));
